import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import UserAvatar, { TIER_TINTS } from "@/components/app/UserAvatar";
import {
  Users, UserPlus, Search, Filter, Download, Trash2, Power, PowerOff,
  Loader2, X, Camera, UploadCloud, ShieldCheck, FileText, CheckSquare, Square, KeyRound, Briefcase, Building2, BadgeCheck, Calendar as CalendarIcon, Award, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import StaffProfileDialog from "@/components/app/StaffProfileDialog";
import { logger } from "@/lib/logger";

const DEFAULT_ROLES = [
  ["super_admin", "Super Admin"],
  ["general_manager", "General Manager"],
  ["property_admin", "Property Admin"],
  ["supervisor", "Supervisor"],
  ["housekeeping", "Housekeeping"],
  ["maintenance", "Maintenance"],
];

const STATUS_META = {
  probation: { label: "Probation", cls: "bg-amber-100 text-amber-800" },
  temporary: { label: "Temporary", cls: "bg-sky-100 text-sky-800" },
  permanent: { label: "Permanent", cls: "bg-emerald-100 text-emerald-800" },
};

const initials = (name) =>
  (name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const downloadCsv = (rows, propNameById) => {
  const header = ["Employee ID", "Name", "Email", "Role", "Department", "Status", "Date of joining", "Active", "Properties"];
  const lines = [header.join(",")].concat(
    rows.map((u) =>
      [
        u.employee_id || "",
        `"${(u.name || "").replace(/"/g, '""')}"`,
        u.email || "",
        u.role || "",
        u.department || "",
        u.employment_status || "",
        u.date_of_joining || "",
        u.active ? "Yes" : "No",
        `"${(u.property_ids || []).map((id) => propNameById[id]).filter(Boolean).join("; ")}"`,
      ].join(",")
    )
  );
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `staff-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

function Avatar({ user, size = 40 }) {
  return <UserAvatar user={user} size={size} />;
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.probation;
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function PhotoPicker({ value, onChange, disabled }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Photo upload failed");
    } finally {
      setBusy(false);
    }
  };

  const previewUrl = value ? `${API}/files?path=${encodeURIComponent(value)}` : null;

  return (
    <div className="flex items-center gap-3">
      {previewUrl ? (
        <img src={previewUrl} alt="Preview" className="w-16 h-16 rounded-full object-cover border-2 border-emerald-500" />
      ) : (
        <div className="w-16 h-16 rounded-full border-2 border-dashed border-zinc-300 bg-zinc-50 flex items-center justify-center text-zinc-400">
          <Camera className="w-5 h-5" />
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} disabled={disabled || busy} data-testid="staff-add-photo-file" />
      <input ref={cameraRef} type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} disabled={disabled || busy} data-testid="staff-add-photo-camera" />
      <div className="flex flex-col gap-1.5">
        <Button type="button" variant="outline" size="sm" onClick={() => cameraRef.current?.click()} disabled={disabled || busy}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Camera className="w-3.5 h-3.5 mr-1" />} Take photo
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={disabled || busy}>
          <UploadCloud className="w-3.5 h-3.5 mr-1" /> Upload
        </Button>
      </div>
    </div>
  );
}

const newStaffEmpty = {
  name: "", email: "", password: "",
  role: "housekeeping", property_ids: [],
  department: "", employment_status: "probation",
  date_of_joining: "", avatar_path: "",
};

export default function Staff() {
  const { user: me, hasPermission } = useAuth();
  const { properties } = useProperty();
  const [users, setUsers] = useState([]);
  const [rolesList, setRolesList] = useState(DEFAULT_ROLES);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", role: "all", department: "all", status: "all", property: "all", active: "active" });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState(newStaffEmpty);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // Bulk-action state (super_admin only)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null); // string action key
  const [bulkPayload, setBulkPayload] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [exportBusy, setExportBusy] = useState(null); // 'xlsx' | 'pdf' | null

  const isSuperAdmin = me?.role === "super_admin";
  const canExport = !!(hasPermission && hasPermission("id_cards"));
  const canSelect = isSuperAdmin || canExport;

  const propNameById = useMemo(() => Object.fromEntries((properties || []).map((p) => [p.id, p.name])), [properties]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, d] = await Promise.all([
        api.get("/users"),
        api.get("/departments").catch(() => ({ data: [] })),
      ]);
      // Defensive: drop pending/rejected self-signups (they're on /pending-approvals)
      // and any row missing id/role.
      setUsers((u.data || []).filter((x) => x && x.id && x.approval_status !== "pending" && x.approval_status !== "rejected"));
      setDepartments(d.data || []);
    } catch (e) {
      logger.error("Failed to load staff", e);
      toast.error(e.response?.data?.detail || "Failed to load staff");
    }
    try {
      const r = await api.get("/roles");
      setRolesList(r.data.map((x) => [x.key, x.name]));
    } catch (e) {
      logger.warn("custom roles unavailable, using defaults", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !(u.name || "").toLowerCase().includes(q) && !(u.email || "").toLowerCase().includes(q) && !(u.employee_id || "").toLowerCase().includes(q)) return false;
      if (filters.role !== "all" && u.role !== filters.role) return false;
      if (filters.department !== "all" && (u.department || "") !== filters.department) return false;
      if (filters.status !== "all" && (u.employment_status || "probation") !== filters.status) return false;
      if (filters.property !== "all" && !(u.property_ids || []).includes(filters.property)) return false;
      if (filters.active === "active" && !u.active) return false;
      if (filters.active === "inactive" && u.active) return false;
      return true;
    });
  }, [users, filters]);

  const counts = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.active).length,
    inactive: users.filter((u) => !u.active).length,
    probation: users.filter((u) => (u.employment_status || "probation") === "probation").length,
  }), [users]);

  const clearFilters = () => setFilters({ q: "", role: "all", department: "all", status: "all", property: "all", active: "active" });
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.q) n++;
    if (filters.role !== "all") n++;
    if (filters.department !== "all") n++;
    if (filters.status !== "all") n++;
    if (filters.property !== "all") n++;
    if (filters.active !== "active") n++;
    return n;
  }, [filters]);

  const submitAdd = async (e) => {
    e.preventDefault();
    if (addForm.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setAdding(true);
    try {
      await api.post("/auth/register", {
        name: addForm.name.trim(),
        email: addForm.email.trim().toLowerCase(),
        password: addForm.password,
        role: addForm.role,
        property_ids: addForm.property_ids,
        date_of_joining: addForm.date_of_joining || null,
        department: addForm.department || null,
        employment_status: addForm.employment_status,
        avatar_path: addForm.avatar_path || null,
      });
      toast.success(`${addForm.name} added to the team`);
      setAddOpen(false);
      setAddForm(newStaffEmpty);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add staff");
    } finally {
      setAdding(false);
    }
  };

  const toggleActive = async (u) => {
    setBusyId(u.id);
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      setUsers((arr) => arr.map((x) => (x.id === u.id ? { ...x, active: !u.active } : x)));
      toast.success(`${u.name} is now ${!u.active ? "active" : "inactive"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async () => {
    if (!confirmDelete) return;
    setBusyId(confirmDelete.id);
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      setUsers((arr) => arr.filter((x) => x.id !== confirmDelete.id));
      toast.success(`${confirmDelete.name} removed`);
      setConfirmDelete(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    } finally {
      setBusyId(null);
    }
  };

  // ----- Bulk-action helpers (super_admin only) -----
  const toggleSelect = (id) => setSelectedIds((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAllOnPage = () => {
    const ids = filtered.map((u) => u.id);
    setSelectedIds((s) => {
      const allSelected = ids.every((id) => s.has(id));
      const next = new Set(s);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const exportProfiles = async (format) => {
    if (selectedIds.size === 0) { toast.error("Select at least one employee"); return; }
    setExportBusy(format);
    try {
      const resp = await api.post(`/users/profiles/export?format=${format}`, {
        user_ids: Array.from(selectedIds),
      }, { responseType: "blob" });
      const mime = format === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const ext = format === "xlsx" ? "xlsx" : "pdf";
      const url = URL.createObjectURL(new Blob([resp.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `employee_profiles_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${selectedIds.size} profile${selectedIds.size === 1 ? "" : "s"} to ${ext.toUpperCase()}`);
    } catch (e) {
      logger.error("profile export", e);
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setExportBusy(null);
    }
  };

  const openBulk = (action) => {
    setBulkAction(action);
    // Reset payload to sensible defaults for the action
    if (action === "set_role") setBulkPayload({ role: "housekeeping" });
    else if (action === "set_department") setBulkPayload({ department: "" });
    else if (action === "set_employment_status") setBulkPayload({ employment_status: "permanent" });
    else if (action === "set_property_ids") setBulkPayload({ property_ids: [] });
    else if (action === "set_date_of_joining") setBulkPayload({ date_of_joining: "" });
    else if (action === "reset_password") setBulkPayload({ new_password: "" });
    else if (action === "issue_appreciation") setBulkPayload({ reason: "" });
    else if (action === "send_announcement") setBulkPayload({ title: "", body: "" });
    else setBulkPayload({});
  };

  const runBulk = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const action = bulkAction.startsWith("activate") ? "set_active" : bulkAction;
      const payload = bulkAction === "activate_on"
        ? { active: true }
        : bulkAction === "activate_off"
          ? { active: false }
          : bulkPayload;
      const { data } = await api.post("/users/bulk", {
        user_ids: Array.from(selectedIds),
        action,
        payload,
      });
      setBulkResult(data);
      const ok = data.succeeded?.length || 0;
      const fail = data.failed?.length || 0;
      if (fail === 0) toast.success(`${ok} updated`);
      else if (ok === 0) toast.error(`Failed for all ${fail}`);
      else toast.success(`${ok} updated · ${fail} failed`);
      // Clear selection on success (or partial success)
      if (ok > 0) {
        setSelectedIds((s) => {
          const next = new Set(s);
          data.succeeded?.forEach((r) => next.delete(r.id));
          return next;
        });
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const selectedCount = selectedIds.size;
  const visibleIds = filtered.map((u) => u.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="label-overline">Team</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Staff</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-lg">
            Every team member across your properties — search, filter, and manage in one place.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => downloadCsv(filtered, propNameById)} variant="outline" disabled={!filtered.length} data-testid="staff-export-csv">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button onClick={() => { setAddForm(newStaffEmpty); setAddOpen(true); }} className="bg-zinc-950 hover:bg-zinc-800" data-testid="staff-add-button">
            <UserPlus className="w-4 h-4 mr-2" /> Add staff
          </Button>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total", value: counts.total },
          { label: "Active", value: counts.active },
          { label: "Inactive", value: counts.inactive },
          { label: "On probation", value: counts.probation },
        ].map((t) => (
          <div key={t.label} className="bg-white border border-zinc-200 rounded-md p-4">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t.label}</div>
            <div className="font-display text-3xl font-black tracking-tight mt-1">{t.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-md p-4" data-testid="staff-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-zinc-500" />
          <span className="text-xs uppercase tracking-widest font-semibold text-zinc-700">Filters</span>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="ml-auto text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1" data-testid="staff-clear-filters">
              <X className="w-3 h-3" /> Clear ({activeFilterCount})
            </button>
          )}
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Search name, email, employee ID…"
                className="pl-9 h-10"
                data-testid="staff-search"
              />
            </div>
          </div>
          <Select value={filters.role} onValueChange={(v) => setFilters({ ...filters, role: v })}>
            <SelectTrigger className="h-10" data-testid="staff-filter-role"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {rolesList.map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.department} onValueChange={(v) => setFilters({ ...filters, department: v })}>
            <SelectTrigger className="h-10" data-testid="staff-filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="h-10" data-testid="staff-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="probation">Probation</SelectItem>
              <SelectItem value="temporary">Temporary</SelectItem>
              <SelectItem value="permanent">Permanent</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.active} onValueChange={(v) => setFilters({ ...filters, active: v })}>
            <SelectTrigger className="h-10" data-testid="staff-filter-active"><SelectValue placeholder="Active" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="inactive">Inactive only</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          {properties?.length > 1 && (
            <Select value={filters.property} onValueChange={(v) => setFilters({ ...filters, property: v })}>
              <SelectTrigger className="h-10 lg:col-span-1" data-testid="staff-filter-property"><SelectValue placeholder="Property" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All properties</SelectItem>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="staff-list">
        {/* Bulk action bar (super_admin gets full set; others with id_cards perm get exports only) */}
        {canSelect && selectedCount > 0 && (
          <div className="bg-zinc-950 text-white px-4 py-2.5 flex items-center flex-wrap gap-2" data-testid="bulk-action-bar">
            <span className="text-sm font-semibold mr-2" data-testid="bulk-selected-count">
              {selectedCount} selected
            </span>
            <button onClick={clearSelection} className="text-xs text-zinc-400 hover:text-white mr-3" data-testid="bulk-clear">
              <X className="w-3.5 h-3.5 inline mr-1" /> Clear
            </button>
            <div className="flex flex-wrap gap-1.5 ml-auto">
              {canExport && (
                <>
                  <Button size="sm" variant="secondary" onClick={() => exportProfiles("xlsx")} disabled={exportBusy === "xlsx"} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="bulk-export-xlsx">
                    {exportBusy === "xlsx" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Export Excel
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => exportProfiles("pdf")} disabled={exportBusy === "pdf"} className="bg-sky-600 text-white hover:bg-sky-700" data-testid="bulk-export-pdf">
                    {exportBusy === "pdf" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />} Export PDF
                  </Button>
                </>
              )}
              {isSuperAdmin && (<>
              <Button size="sm" variant="secondary" onClick={() => openBulk("activate_on")} data-testid="bulk-activate">
                <Power className="w-3.5 h-3.5 mr-1" /> Activate
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openBulk("activate_off")} data-testid="bulk-deactivate">
                <PowerOff className="w-3.5 h-3.5 mr-1" /> Deactivate
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openBulk("set_role")} data-testid="bulk-role">
                <Briefcase className="w-3.5 h-3.5 mr-1" /> Role
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openBulk("set_department")} data-testid="bulk-department">
                <Building2 className="w-3.5 h-3.5 mr-1" /> Department
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openBulk("set_employment_status")} data-testid="bulk-employment-status">
                <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Status
              </Button>
              {properties?.length > 0 && (
                <Button size="sm" variant="secondary" onClick={() => openBulk("set_property_ids")} data-testid="bulk-property">
                  <Building2 className="w-3.5 h-3.5 mr-1" /> Properties
                </Button>
              )}
              <Button size="sm" variant="secondary" onClick={() => openBulk("set_date_of_joining")} data-testid="bulk-doj">
                <CalendarIcon className="w-3.5 h-3.5 mr-1" /> Date of Joining
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openBulk("reset_password")} data-testid="bulk-password">
                <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset password
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openBulk("verify")} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="bulk-verify">
                <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Verify KYC
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openBulk("issue_appreciation")} className="bg-amber-500 text-white hover:bg-amber-600" data-testid="bulk-appreciation">
                <Award className="w-3.5 h-3.5 mr-1" /> Appreciation
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openBulk("send_announcement")} className="bg-sky-600 text-white hover:bg-sky-700" data-testid="bulk-announcement">
                <Megaphone className="w-3.5 h-3.5 mr-1" /> Announcement
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openBulk("delete")} className="bg-red-600 text-white hover:bg-red-700" data-testid="bulk-delete">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
              </>)}
            </div>
          </div>
        )}
        {(() => {
          if (loading) return <div className="p-10 flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
          if (filtered.length === 0) {
            return (
              <div className="p-10 text-center" data-testid="staff-empty">
                <Users className="w-8 h-8 mx-auto text-zinc-300" strokeWidth={1.5} />
                <div className="mt-2 text-zinc-500 text-sm">{users.length === 0 ? "No staff yet — click 'Add staff' to onboard your first team member." : "No staff match these filters."}</div>
              </div>
            );
          }
          return (
            <div className="divide-y divide-zinc-100">
              {canSelect && (
                <div className="px-4 py-2 bg-zinc-50/60 flex items-center gap-4 text-[11px] uppercase tracking-widest text-zinc-500" data-testid="staff-list-header">
                  <button
                    onClick={selectAllOnPage}
                    className="w-5 h-5 flex items-center justify-center hover:text-zinc-900"
                    data-testid="bulk-select-all"
                    aria-label={allVisibleSelected ? "Deselect all visible" : "Select all visible"}
                  >
                    {allVisibleSelected ? <CheckSquare className="w-4 h-4 text-zinc-900" /> : <Square className="w-4 h-4" />}
                  </button>
                  <div className="w-11" aria-hidden />
                  <span className="flex-1">Member · Select all on this view ({filtered.length})</span>
                  <span className="hidden md:block min-w-[120px] shrink-0">Role</span>
                  <span className="hidden lg:block min-w-[120px] shrink-0">Department</span>
                  <span className="shrink-0">Actions</span>
                </div>
              )}
              {filtered.map((u) => {
                const tint = TIER_TINTS[u.conduct_tier] || TIER_TINTS.none;
                return (
                <div key={u.id} className={`flex flex-col md:flex-row md:items-center gap-3 md:gap-4 px-3 md:px-4 py-3 hover:bg-zinc-50 transition-colors ${tint.row} ${selectedIds.has(u.id) ? "bg-zinc-50" : ""}`} data-testid={`staff-row-${u.id}`} data-conduct-tier={u.conduct_tier || "none"}>
                  <div className="flex items-center gap-3 md:gap-4 min-w-0 md:flex-1">
                    {canSelect && (
                      <button
                        onClick={() => toggleSelect(u.id)}
                        className="w-5 h-5 flex items-center justify-center text-zinc-400 hover:text-zinc-900 shrink-0"
                        data-testid={`staff-select-${u.id}`}
                        aria-label={selectedIds.has(u.id) ? `Deselect ${u.name}` : `Select ${u.name}`}
                      >
                        {selectedIds.has(u.id) ? <CheckSquare className="w-4 h-4 text-zinc-900" /> : <Square className="w-4 h-4" />}
                      </button>
                    )}
                    <Avatar user={u} size={44} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => setSelectedUserId(u.id)}
                          className="font-semibold text-zinc-900 hover:underline text-left truncate max-w-[60vw] md:max-w-none"
                          data-testid={`staff-row-name-${u.id}`}
                        >
                          {u.name || "—"}
                        </button>
                        <StatusBadge status={u.employment_status} />
                        {!u.active && (
                          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-zinc-100 text-zinc-600">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-zinc-500 flex flex-wrap gap-x-3 mt-0.5">
                        <span className="font-mono">{u.employee_id || "—"}</span>
                        <span className="truncate max-w-[60vw]">{u.email || "—"}</span>
                        {u.date_of_joining && <span className="hidden sm:inline">joined {u.date_of_joining}</span>}
                      </div>
                      {/* Mobile-only role + department chips */}
                      <div className="md:hidden flex flex-wrap gap-1.5 mt-1.5">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-100 text-[10px] uppercase tracking-wider text-zinc-700">
                          <span className="text-zinc-400">Role</span>
                          <span className="font-semibold capitalize">{(u.role || "—").replace(/_/g, " ")}</span>
                        </span>
                        {u.department && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-100 text-[10px] uppercase tracking-wider text-zinc-700">
                            <span className="text-zinc-400">Dept</span>
                            <span className="font-semibold">{u.department}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Role + Department as labeled columns (tablet/desktop only) */}
                  <div className="hidden md:block min-w-[120px] shrink-0" data-testid={`staff-row-role-${u.id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400">Role</div>
                    <div className="text-sm text-zinc-900 font-medium capitalize truncate">
                      {(u.role || "—").replace(/_/g, " ")}
                    </div>
                  </div>
                  <div className="hidden lg:block min-w-[120px] shrink-0" data-testid={`staff-row-department-${u.id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400">Department</div>
                    <div className="text-sm text-zinc-900 font-medium truncate">
                      {u.department || "—"}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 shrink-0 md:ml-auto pl-8 md:pl-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedUserId(u.id)}
                      data-testid={`staff-profile-${u.id}`}
                    >
                      <FileText className="w-3.5 h-3.5 md:mr-1" /> <span className="hidden md:inline">Profile</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleActive(u)}
                      disabled={busyId === u.id}
                      className={u.active ? "text-zinc-700" : "text-emerald-700 border-emerald-200"}
                      data-testid={`staff-toggle-${u.id}`}
                    >
                      {busyId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : u.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                      <span className="hidden md:inline ml-1">{u.active ? "Deactivate" : "Activate"}</span>
                    </Button>
                    {isSuperAdmin && u.id !== me?.id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmDelete(u)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid={`staff-delete-${u.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );})}
            </div>
          );
        })()}
      </div>

      {/* Add staff dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { if (!adding) setAddOpen(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="staff-add-dialog">
          <DialogHeader>
            <DialogTitle>Add a team member</DialogTitle>
            <DialogDescription>
              They'll be marked active immediately. They'll still be prompted to add a photo + KYC at first login if you skip them here.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdd} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-600">Photo (optional)</Label>
              <div className="mt-2">
                <PhotoPicker value={addForm.avatar_path} onChange={(p) => setAddForm({ ...addForm, avatar_path: p })} disabled={adding} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-600">Full name</Label>
                <Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} required minLength={2} className="mt-1 h-10" data-testid="staff-add-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-600">Email</Label>
                <Input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} required className="mt-1 h-10" data-testid="staff-add-email" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-600">Temporary password</Label>
                <Input type="text" value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} required minLength={6} className="mt-1 h-10" placeholder="≥ 6 characters" data-testid="staff-add-password" />
                <p className="text-[11px] text-zinc-500 mt-1">Share this securely — the user can change it from My Profile.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-600">Date of joining</Label>
                <Input type="date" value={addForm.date_of_joining} onChange={(e) => setAddForm({ ...addForm, date_of_joining: e.target.value })} max={new Date().toISOString().slice(0, 10)} className="mt-1 h-10" data-testid="staff-add-doj" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-600">Role</Label>
                <Select value={addForm.role} onValueChange={(v) => setAddForm({ ...addForm, role: v })}>
                  <SelectTrigger className="mt-1 h-10" data-testid="staff-add-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{rolesList.map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-600">Department</Label>
                <Select value={addForm.department || "__none"} onValueChange={(v) => setAddForm({ ...addForm, department: v === "__none" ? "" : v })}>
                  <SelectTrigger className="mt-1 h-10" data-testid="staff-add-department"><SelectValue placeholder="Not set" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— Not set —</SelectItem>
                    {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-600">Employment status</Label>
              <Select value={addForm.employment_status} onValueChange={(v) => setAddForm({ ...addForm, employment_status: v })}>
                <SelectTrigger className="mt-1 h-10" data-testid="staff-add-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="probation">Probation</SelectItem>
                  <SelectItem value="temporary">Temporary</SelectItem>
                  <SelectItem value="permanent">Permanent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {properties && properties.length > 1 && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-600">Property access</Label>
                <div className="mt-2 max-h-32 overflow-auto border border-zinc-200 rounded p-2">
                  {properties.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 px-2 py-1 hover:bg-zinc-50 rounded text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={addForm.property_ids.includes(p.id)}
                        onChange={() => setAddForm((f) => ({ ...f, property_ids: f.property_ids.includes(p.id) ? f.property_ids.filter((x) => x !== p.id) : [...f.property_ids, p.id] }))}
                        data-testid={`staff-add-property-${p.id}`}
                      />
                      <span>{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="submit" disabled={adding} className="bg-zinc-950 hover:bg-zinc-800" data-testid="staff-add-submit">
                {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Create account
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the account and all associated records. Use 'Deactivate' instead if you only want to revoke access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="staff-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteUser} className="bg-red-600 hover:bg-red-700" data-testid="staff-delete-confirm">
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Profile drawer (existing dialog reused) */}
      <StaffProfileDialog
        userId={selectedUserId}
        open={!!selectedUserId}
        onOpenChange={(o) => !o && setSelectedUserId(null)}
        onChanged={load}
      />

      {/* Bulk action dialog */}
      <Dialog open={!!bulkAction} onOpenChange={(o) => { if (!o && !bulkBusy) { setBulkAction(null); setBulkResult(null); } }}>
        <DialogContent className="max-w-md" data-testid="bulk-action-dialog">
          <DialogHeader>
            <DialogTitle data-testid="bulk-action-title">
              {bulkAction === "activate_on" && `Activate ${selectedCount} staff`}
              {bulkAction === "activate_off" && `Deactivate ${selectedCount} staff`}
              {bulkAction === "set_role" && `Change role for ${selectedCount} staff`}
              {bulkAction === "set_department" && `Set department for ${selectedCount} staff`}
              {bulkAction === "set_employment_status" && `Set employment status for ${selectedCount} staff`}
              {bulkAction === "set_property_ids" && `Set property access for ${selectedCount} staff`}
              {bulkAction === "set_date_of_joining" && `Set Date of Joining for ${selectedCount} staff`}
              {bulkAction === "reset_password" && `Reset password for ${selectedCount} staff`}
              {bulkAction === "verify" && `Verify KYC for ${selectedCount} staff`}
              {bulkAction === "issue_appreciation" && `Issue appreciation to ${selectedCount} staff`}
              {bulkAction === "send_announcement" && `Send announcement to ${selectedCount} staff`}
              {bulkAction === "delete" && `Delete ${selectedCount} staff`}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === "activate_off" && "Deactivated members can still sign in but are routed to the apology screen. Use this for short-term suspensions."}
              {bulkAction === "delete" && "This permanently deletes accounts and all associated records. Use Deactivate instead unless you really want to remove them."}
              {bulkAction === "reset_password" && "Share the new temporary password with each member privately. They can change it from My Profile."}
              {bulkAction === "verify" && "Only members with complete KYC will be verified. Incomplete profiles will be reported as failures."}
              {bulkAction === "issue_appreciation" && "Each selected member receives an appreciation with the same reason. 3 appreciations clear active warnings (single-issue policy still applies)."}
              {bulkAction === "send_announcement" && "Pushes a notification to every selected employee. They will see it in the Bell + receive a browser push if enabled."}
              {!["activate_off", "delete", "reset_password", "verify", "issue_appreciation", "send_announcement"].includes(bulkAction || "") && "Pick a value and confirm. Each row is processed independently — successes and failures are reported back."}
            </DialogDescription>
          </DialogHeader>

          {bulkAction === "set_role" && (
            <div>
              <Label>Role</Label>
              <Select value={bulkPayload.role} onValueChange={(v) => setBulkPayload({ role: v })}>
                <SelectTrigger className="mt-1 h-10" data-testid="bulk-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{rolesList.map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {bulkAction === "set_department" && (
            <div>
              <Label>Department</Label>
              <Select value={bulkPayload.department || "__none"} onValueChange={(v) => setBulkPayload({ department: v === "__none" ? "" : v })}>
                <SelectTrigger className="mt-1 h-10" data-testid="bulk-department-select"><SelectValue placeholder="Not set" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Clear department —</SelectItem>
                  {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {bulkAction === "set_employment_status" && (
            <div>
              <Label>Employment status</Label>
              <Select value={bulkPayload.employment_status} onValueChange={(v) => setBulkPayload({ employment_status: v })}>
                <SelectTrigger className="mt-1 h-10" data-testid="bulk-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="probation">Probation</SelectItem>
                  <SelectItem value="temporary">Temporary</SelectItem>
                  <SelectItem value="permanent">Permanent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {bulkAction === "set_property_ids" && (
            <div>
              <Label>Property access (replaces existing)</Label>
              <div className="mt-2 max-h-48 overflow-auto border border-zinc-200 rounded p-2">
                {properties.length === 0 && <div className="text-xs text-zinc-500 px-1 py-2">No properties available.</div>}
                {properties.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 rounded cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={(bulkPayload.property_ids || []).includes(p.id)}
                      onChange={() => setBulkPayload((b) => ({
                        ...b,
                        property_ids: (b.property_ids || []).includes(p.id)
                          ? b.property_ids.filter((x) => x !== p.id)
                          : [...(b.property_ids || []), p.id],
                      }))}
                      data-testid={`bulk-property-${p.id}`}
                    />
                    <span>{p.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">This REPLACES each member's current property access.</p>
            </div>
          )}
          {bulkAction === "set_date_of_joining" && (
            <div>
              <Label>Date of Joining</Label>
              <Input
                type="date"
                value={bulkPayload.date_of_joining || ""}
                onChange={(e) => setBulkPayload({ date_of_joining: e.target.value })}
                max={new Date().toISOString().slice(0, 10)}
                className="mt-1 h-10"
                data-testid="bulk-doj-input"
              />
              <p className="text-[11px] text-zinc-500 mt-1">Leave blank to clear the DoJ for selected members.</p>
            </div>
          )}
          {bulkAction === "reset_password" && (
            <div>
              <Label>New temporary password</Label>
              <Input
                value={bulkPayload.new_password || ""}
                onChange={(e) => setBulkPayload({ new_password: e.target.value })}
                placeholder="≥ 6 characters"
                minLength={6}
                className="mt-1 h-10"
                data-testid="bulk-password-input"
              />
              <p className="text-[11px] text-zinc-500 mt-1">All selected members will have this password until they change it.</p>
            </div>
          )}
          {bulkAction === "issue_appreciation" && (
            <div>
              <Label>Reason</Label>
              <Input
                value={bulkPayload.reason || ""}
                onChange={(e) => setBulkPayload({ reason: e.target.value })}
                placeholder="e.g. Excellent guest review for Diwali shift"
                className="mt-1 h-10"
                data-testid="bulk-appreciation-reason"
              />
              <p className="text-[11px] text-zinc-500 mt-1">Each selected member receives an appreciation with this reason.</p>
            </div>
          )}
          {bulkAction === "send_announcement" && (
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input
                  value={bulkPayload.title || ""}
                  onChange={(e) => setBulkPayload({ ...bulkPayload, title: e.target.value })}
                  placeholder="e.g. Office closed on Holi"
                  maxLength={120}
                  className="mt-1 h-10"
                  data-testid="bulk-announcement-title"
                />
              </div>
              <div>
                <Label>Message</Label>
                <Input
                  value={bulkPayload.body || ""}
                  onChange={(e) => setBulkPayload({ ...bulkPayload, body: e.target.value })}
                  placeholder="Short message that appears in the notification"
                  maxLength={500}
                  className="mt-1 h-10"
                  data-testid="bulk-announcement-body"
                />
              </div>
            </div>
          )}

          {/* Results panel after run */}
          {bulkResult && (
            <div className="border-t border-zinc-200 pt-4 mt-2 text-sm space-y-2" data-testid="bulk-result-panel">
              <div className="flex items-center gap-4">
                <span className="text-emerald-700"><CheckSquare className="w-4 h-4 inline mr-1" />{bulkResult.succeeded?.length || 0} updated</span>
                <span className="text-red-700"><X className="w-4 h-4 inline mr-1" />{bulkResult.failed?.length || 0} failed</span>
              </div>
              {bulkResult.failed?.length > 0 && (
                <div className="max-h-40 overflow-auto border border-red-100 rounded p-2 bg-red-50/40">
                  {bulkResult.failed.map((f) => (
                    <div key={f.id} className="text-xs text-red-700 py-0.5" data-testid={`bulk-fail-${f.id}`}>
                      <span className="font-semibold">{f.name || f.id}:</span> {f.reason}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {bulkResult ? (
              <Button onClick={() => { setBulkAction(null); setBulkResult(null); }} data-testid="bulk-close" className="bg-zinc-950 hover:bg-zinc-800">
                Done
              </Button>
            ) : (
              <Button
                onClick={runBulk}
                disabled={bulkBusy}
                className={["delete"].includes(bulkAction) ? "bg-red-600 hover:bg-red-700" : "bg-zinc-950 hover:bg-zinc-800"}
                data-testid="bulk-confirm"
              >
                {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                Confirm
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
